import { useMemo, useState } from 'react';
import { DeleteButton, Empty, ErrorBanner, Field, Loading, Modal } from '../../components/ui';
import { api } from '../../lib/api';
import { useResource } from '../../lib/hooks';
import type { AmQuestion, AmQuestionSet } from '../../lib/types';

const QUESTION_TYPES = [
  ['text', 'Free text'],
  ['yesno', 'Yes / No / Partial'],
  ['yesnounknown', 'Yes / No / Partial / Unknown'],
  ['scale', 'Scale 1-5'],
] as const;

/**
 * The audit question bank.
 *
 * Built-in questions are read-only: they are what makes two audits comparable,
 * and an audit whose questions changed between runs cannot be trended. Custom
 * questions can be added, edited and removed — until one has been answered, at
 * which point removing it would destroy the answer.
 *
 * The yes/no/partial type matters more than it looks. It is what makes a
 * template scoreable as a control review, which is what unlocks the scorecard
 * and the risk-acceptance form.
 */
export function AmQuestions() {
  const sets = useResource<AmQuestionSet[]>('/am/questions');
  const [open, setOpen] = useState<string | null>(null);
  const questions = useResource<AmQuestion[]>(
    open ? `/am/questions/${encodeURIComponent(open)}` : null,
  );
  const [newTemplate, setNewTemplate] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<AmQuestion> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const categories = useMemo(
    () => [...new Set((questions.data ?? []).map((q) => q.Category).filter(Boolean))] as string[],
    [questions.data],
  );

  const grouped = useMemo(() => {
    const map = new Map<string, AmQuestion[]>();
    for (const q of questions.data ?? []) {
      const key = q.Category ?? 'Other';
      map.set(key, [...(map.get(key) ?? []), q]);
    }
    return [...map.entries()];
  }, [questions.data]);

  async function createTemplate(name: string) {
    try {
      await api.post('/am/question-sets', { name });
      setNewTemplate(null);
      sets.reload();
      setOpen(name);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the template');
    }
  }

  async function deleteTemplate(name: string) {
    try {
      await api.del(`/am/question-sets/${encodeURIComponent(name)}`);
      setOpen(null);
      sets.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete the template');
    }
  }

  async function deleteQuestion(id: string) {
    try {
      await api.del(`/am/questions/${id}`);
      questions.reload();
      sets.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete the question');
    }
  }

  if (sets.loading) return <Loading label="Loading question templates" />;
  if (sets.error) return <ErrorBanner message={sets.error} />;

  const all = sets.data ?? [];
  const current = all.find((s) => s.SetName === open);

  return (
    <>
      {error && <ErrorBanner message={error} />}

      <div className="row-between wrap mb-4">
        <p className="small muted">
          {all.length} template{all.length === 1 ? '' : 's'} ·{' '}
          {all.reduce((sum, s) => sum + s.Questions, 0)} questions in total. Open one to see its
          questions.
        </p>
        <div className="row">
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setNewTemplate('')}>
            New template
          </button>
          {open && (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setForm({ SetName: open, QuestionType: 'text', Category: '', Text: '' })}
            >
              Add a question
            </button>
          )}
        </div>
      </div>

      <div className="stack">
        {all.map((set) => (
          <section key={set.SetName} className="card">
            <button
              type="button"
              className="card-head as-toggle"
              onClick={() => setOpen(open === set.SetName ? null : set.SetName)}
            >
              <div className="grow">
                <span className="eyebrow">
                  {set.Categories} categor{set.Categories === 1 ? 'y' : 'ies'}
                  {set.IsDefault ? ' · default' : ''}
                </span>
                <h2>{set.SetName}</h2>
                <p className="sub">
                  {set.Questions} question{set.Questions === 1 ? '' : 's'}
                  {set.CustomQuestions > 0 ? ` · ${set.CustomQuestions} custom` : ''}
                  {set.Audits > 0 ? ` · used by ${set.Audits} audit${set.Audits === 1 ? '' : 's'}` : ''}
                </p>
              </div>
              <span className="xs muted">{open === set.SetName ? '▲' : '▼'}</span>
            </button>

            {open === set.SetName && (
              <div className="card-body">
                {/* A template that is neither the default nor in use is the only
                    one safe to delete, and saying so beats a rejected click. */}
                {!set.IsDefault && set.Audits === 0 && (
                  <div className="row-between mb-4">
                    <span className="xs muted">
                      Not the default and not used by any audit, so it can be removed.
                    </span>
                    <DeleteButton
                      label="Delete template"
                      what={`the template "${set.SetName}" and its questions`}
                      onConfirm={() => void deleteTemplate(set.SetName)}
                    />
                  </div>
                )}

                {questions.loading ? (
                  <Loading />
                ) : set.Questions === 0 ? (
                  <Empty title="No questions yet">
                    <p>Add custom questions to build this template out, then run an audit against it.</p>
                  </Empty>
                ) : (
                  grouped.map(([category, items]) => (
                    <div key={category}>
                      <div className="am-category">
                        {category} <span className="muted">({items.length})</span>
                      </div>
                      {items.map((q) => (
                        <div key={q.AmQuestionId} className="mini-row">
                          <div className="grow">
                            <div className="small">{q.Text}</div>
                            <div className="xs muted mt-2">
                              {QUESTION_TYPES.find(([v]) => v === q.QuestionType)?.[1] ?? 'Free text'}
                              {q.IsCustom && ' · custom'}
                            </div>
                            {q.Guidance && <div className="xs muted mt-2">{q.Guidance}</div>}
                          </div>
                          {q.IsCustom && (
                            <span className="row">
                              <button
                                type="button"
                                className="btn btn-ghost btn-sm"
                                onClick={() => setForm(q)}
                              >
                                Edit
                              </button>
                              <DeleteButton
                                what="this custom question"
                                onConfirm={() => void deleteQuestion(q.AmQuestionId)}
                              />
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  ))
                )}
              </div>
            )}
          </section>
        ))}
      </div>

      {newTemplate !== null && (
        <NewTemplateModal
          onClose={() => setNewTemplate(null)}
          onCreate={(name) => void createTemplate(name)}
        />
      )}

      {form && (
        <QuestionModal
          question={form}
          setName={form.SetName ?? open ?? ''}
          categories={categories}
          onClose={() => setForm(null)}
          onSaved={() => {
            setForm(null);
            questions.reload();
            sets.reload();
          }}
        />
      )}

      {!current && all.length === 0 && (
        <Empty title="No templates">
          <p>Create a template, then add the questions your audits should ask.</p>
        </Empty>
      )}
    </>
  );
}

function NewTemplateModal({
  onClose,
  onCreate,
}: {
  onClose: () => void;
  onCreate: (name: string) => void;
}) {
  const [name, setName] = useState('');
  return (
    <Modal
      title="New template"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!name.trim()}
            onClick={() => onCreate(name.trim())}
          >
            Create
          </button>
        </>
      }
    >
      <Field
        label="Template name"
        hint="It starts empty. Add questions, then pick it when you run an audit."
      >
        <input
          className="input"
          autoFocus
          placeholder="e.g. Security deep dive"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
    </Modal>
  );
}

function QuestionModal({
  question,
  setName,
  categories,
  onClose,
  onSaved,
}: {
  question: Partial<AmQuestion>;
  setName: string;
  categories: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState(question);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setSaving(true);
    setError(null);
    const body = {
      category: (form.Category ?? '').trim(),
      text: (form.Text ?? '').trim(),
      guidance: form.Guidance ?? '',
      questionType: form.QuestionType ?? 'text',
    };
    try {
      if (form.AmQuestionId) await api.put(`/am/questions/${form.AmQuestionId}`, body);
      else await api.post('/am/questions', { ...body, setName });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the question');
      setSaving(false);
    }
  }

  return (
    <Modal
      title={form.AmQuestionId ? 'Edit question' : `Add a question to "${setName}"`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !(form.Text ?? '').trim()}
            onClick={() => void submit()}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <Field label="Question">
        <textarea
          className="textarea"
          rows={3}
          value={form.Text ?? ''}
          onChange={(e) => setForm({ ...form, Text: e.target.value })}
        />
      </Field>
      <div className="form-grid">
        <Field label="Category" hint="Groups the question in the audit. Defaults to Custom.">
          <input
            className="input"
            list="am-question-categories"
            value={form.Category ?? ''}
            onChange={(e) => setForm({ ...form, Category: e.target.value })}
          />
          <datalist id="am-question-categories">
            {categories.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </Field>
        <Field
          label="Answer type"
          hint="Yes / No / Partial is what makes a template scoreable as a control review."
        >
          <select
            className="select"
            value={form.QuestionType ?? 'text'}
            onChange={(e) => setForm({ ...form, QuestionType: e.target.value })}
          >
            {QUESTION_TYPES.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Guidance" hint="Shown under the question while the audit is being answered.">
        <textarea
          className="textarea"
          rows={2}
          value={form.Guidance ?? ''}
          onChange={(e) => setForm({ ...form, Guidance: e.target.value })}
        />
      </Field>
    </Modal>
  );
}
